import * as React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Card from "react-bootstrap/Card";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";
import { useQuery } from "@apollo/client";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import apiClient from "api";
import { gettext } from "../i18n";
import { GET_ORGANIZATION_MAP } from "../utils/users";
import MapLegend from "../components/map/MapLegend";
import {
  TILT_AXIS_META,
  getTiltAxisLayout,
  getEffectiveTiltAngles,
  type TiltAxisConfig,
} from "../components/map/MapLegend";
import tiltmeterImage from "theme/assets/img/stations/tiltmeterIcon.webp";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "";

const mapContainerStyle = {
  width: "100%",
};

const mapOptions = {
  mapTypeId: "satellite",
  styles: [
    {
      featureType: "poi",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "transit",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "poi.business",
      stylers: [{ visibility: "off" }],
    },
  ],
};

const defaultCenter = {
  lat: 60.1699,
  lng: 24.9384,
};

const ARROW_DIRECTIONS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"] as const;
const ARROW_LENGTHS = ["short", "medium", "long"] as const;

type ArrowDirection = (typeof ARROW_DIRECTIONS)[number];
type ArrowLength = (typeof ARROW_LENGTHS)[number];

const DIRECTION_ROTATION: Record<ArrowDirection, number> = {
  N: 0,
  NE: 45,
  E: 90,
  SE: 135,
  S: 180,
  SW: 225,
  W: 270,
  NW: 315,
};

const LENGTH_SCALE: Record<ArrowLength, number> = {
  short: 0.7,
  medium: 1.0,
  long: 1.3,
};

const LENGTH_COLORS: Record<ArrowLength, string> = {
  short: "#22c55e",
  medium: "#eab308",
  long: "#ef4444",
};

const ARROW_SIZE = 48;

const MAP_BOTTOM_OFFSET = 24;
const MIN_MAP_HEIGHT = 320;

const generateArrowSvg = (
  direction: ArrowDirection,
  length: ArrowLength,
): string => {
  const rotation = DIRECTION_ROTATION[direction];
  const scale = LENGTH_SCALE[length];
  const color = LENGTH_COLORS[length];
  const size = ARROW_SIZE;
  const center = size / 2;

  const arrowPath = `
    M ${center} ${center - 18 * scale}
    L ${center + 10 * scale} ${center + 6 * scale}
    L ${center + 4 * scale} ${center + 6 * scale}
    L ${center + 4 * scale} ${center + 18 * scale}
    L ${center - 4 * scale} ${center + 18 * scale}
    L ${center - 4 * scale} ${center + 6 * scale}
    L ${center - 10 * scale} ${center + 6 * scale}
    Z
  `;

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <g transform="rotate(${rotation} ${center} ${center})">
        <path d="${arrowPath}" fill="${color}" stroke="#000" stroke-width="1.5"/>
      </g>
    </svg>
  `;

  const encoded = encodeURIComponent(svg.trim());
  return `data:image/svg+xml;charset=utf-8,${encoded}`;
};

const createArrowMarkerIcon = (
  direction: ArrowDirection,
  length: ArrowLength,
): google.maps.Icon | null => {
  if (typeof window === "undefined" || !window.google?.maps) {
    return null;
  }

  const size = ARROW_SIZE;
  const anchor = size / 2;

  return {
    url: generateArrowSvg(direction, length),
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(anchor, anchor),
  };
};

const TILTMETER_MARKER_WIDTH = 48;
const TILTMETER_MARKER_HEIGHT = 48;

const createTiltmeterMarkerIcon = (): google.maps.Icon | null => {
  if (typeof window === "undefined" || !window.google?.maps) {
    return null;
  }

  return {
    url: tiltmeterImage,
    scaledSize: new window.google.maps.Size(
      TILTMETER_MARKER_WIDTH,
      TILTMETER_MARKER_HEIGHT,
    ),
    anchor: new window.google.maps.Point(
      TILTMETER_MARKER_WIDTH / 2,
      TILTMETER_MARKER_HEIGHT / 2,
    ),
  };
};

const hasArrowData = (station: Station): boolean => {
  return (
    station.arrowData != null &&
    ARROW_DIRECTIONS.includes(
      station.arrowData.directionCategory as ArrowDirection,
    ) &&
    ARROW_LENGTHS.includes(station.arrowData.lengthCategory as ArrowLength)
  );
};

interface TiltData {
  timestamp: string;
  xAxis: number;
  yAxis: number;
  zAxis: number;
  xAxisStd: number;
  yAxisStd: number;
  zAxisStd: number;
  temperature: number;
}

interface ArrowData {
  directionCategory: string;
  lengthCategory: string;
  directionValue: number;
  quantityValue: number;
  timestamp: string;
}

interface Station {
  id: number;
  identifier: string;
  name: string;
  lat: number;
  lon: number;
  stationType: "gnss" | "tiltmeter" | null;
  latestTilt: TiltData | null;
  arrowData: ArrowData | null;
  tiltAxisAngles: Record<string, number> | null;
  tiltAxisLayout: string | null;
}

interface MapStationsResponse {
  stations: Station[];
}

const useMapStations = (mapId: string | undefined) => {
  const [stations, setStations] = React.useState<Station[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const fetchStations = React.useCallback(async () => {
    if (!mapId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await apiClient.request(
        `/organization-admin/map/stations?orgMapId=${mapId}`,
      );
      const data = result as MapStationsResponse;
      setStations(data.stations ?? []);
    } catch (e) {
      console.error(e);
      setError(gettext("Failed to load stations"));
    } finally {
      setLoading(false);
    }
  }, [mapId]);

  React.useEffect(() => {
    fetchStations();
  }, [fetchStations]);

  return { stations, loading, error, refetch: fetchStations };
};

const useFillViewportHeight = (ready: boolean) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = React.useState(MIN_MAP_HEIGHT);

  React.useEffect(() => {
    if (!ready) {
      return;
    }

    const element = wrapperRef.current;
    if (!element) {
      return;
    }

    const updateHeight = () => {
      const topOffset = element.getBoundingClientRect().top;
      const available = window.innerHeight - topOffset - MAP_BOTTOM_OFFSET;
      setHeight(Math.max(available, MIN_MAP_HEIGHT));
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(element);
    window.addEventListener("resize", updateHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [ready]);

  return { wrapperRef, height };
};

const getMapCenter = (stations: Station[]): { lat: number; lng: number } => {
  if (stations.length === 0) {
    return defaultCenter;
  }

  const validStations = stations.filter((s) => s.lat && s.lon);
  if (validStations.length === 0) {
    return defaultCenter;
  }

  const latSum = validStations.reduce((sum, s) => sum + s.lat, 0);
  const lngSum = validStations.reduce((sum, s) => sum + s.lon, 0);

  return {
    lat: latSum / validStations.length,
    lng: lngSum / validStations.length,
  };
};

const formatStdValue = (stdValue: number | undefined): string => {
  if (stdValue === undefined) {
    return "—";
  }
  if (stdValue < 1) {
    return "<1°";
  }
  return `${stdValue.toFixed(1)}°`;
};

const TiltValueDisplay: React.FC<{
  label: string;
  stdValue: number | undefined;
}> = ({ label, stdValue }) => {
  const formattedStd = formatStdValue(stdValue);

  return (
    <div className="mb-1">
      <span className="fw-bold">{label}:</span>{" "}
      <span className="text-muted">{formattedStd}</span>
    </div>
  );
};

const infoWindowStyles = `
  .gm-style-iw-c {
    padding: 0 !important;
  }
  .gm-style-iw-d {
    overflow: auto !important;
    padding: 8px !important;
  }
  .gm-style-iw-chr {
    position: absolute !important;
    top: 4px !important;
    right: 4px !important;
    z-index: 1 !important;
  }
`;

const StationInfoWindow: React.FC<{
  station: Station;
  onClose: () => void;
}> = ({ station, onClose }) => {
  const tilt = station.latestTilt;

  return (
    <InfoWindow
      position={{ lat: station.lat, lng: station.lon }}
      onCloseClick={onClose}
    >
      <div style={{ minWidth: "180px" }}>
        <style>{infoWindowStyles}</style>
        <h6 className="mb-1">
          <Link to={`/stations/${station.identifier}`}>{station.name}</Link>
        </h6>

        {tilt ? (
          <>
            <TiltValueDisplay label="X" stdValue={tilt.xAxisStd} />
            <TiltValueDisplay label="Y" stdValue={tilt.yAxisStd} />
            <TiltValueDisplay label="Z" stdValue={tilt.zAxisStd} />
            <div className="text-muted small">
              {new Date(tilt.timestamp).toLocaleString()}
            </div>
          </>
        ) : (
          <p className="text-muted small mb-0">
            {gettext("No tilt data available")}
          </p>
        )}
      </div>
    </InfoWindow>
  );
};

const OrganizationMapPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { stations, loading, error } = useMapStations(id);
  const { data: mapData } = useQuery(GET_ORGANIZATION_MAP, {
    variables: { id: parseInt(id ?? "0", 10) },
    skip: !id,
  });
  const [closedStationIds, setClosedStationIds] = React.useState<Set<number>>(
    new Set(),
  );
  const [mapInstance, setMapInstance] = React.useState<any>(null);

  const isInfoWindowOpen = (stationId: number): boolean => {
    return !closedStationIds.has(stationId);
  };

  const toggleInfoWindow = (stationId: number): void => {
    setClosedStationIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(stationId)) {
        newSet.delete(stationId);
      } else {
        newSet.add(stationId);
      }
      return newSet;
    });
  };

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const mapReady = isLoaded && !!mapData;
  const { wrapperRef, height: mapHeight } = useFillViewportHeight(mapReady);

  const center = React.useMemo(() => getMapCenter(stations), [stations]);

  const hasGnssStations = React.useMemo(
    () => stations.some((s) => s.stationType === "gnss"),
    [stations],
  );
  const hasTiltmeterStations = React.useMemo(
    () => stations.some((s) => s.stationType === "tiltmeter"),
    [stations],
  );

  const { tiltAxes, conflictingTiltStations } = React.useMemo(() => {
    const tiltmeterStations = stations.filter(
      (station) => station.stationType === "tiltmeter",
    );
    const configuredStation = tiltmeterStations.find(
      (station) => station.tiltAxisAngles,
    );
    if (!configuredStation) {
      return {
        tiltAxes: undefined as TiltAxisConfig[] | undefined,
        conflictingTiltStations: [] as Station[],
      };
    }

    const referenceLayout = getTiltAxisLayout(configuredStation.tiltAxisLayout);
    const referenceAngles = getEffectiveTiltAngles(
      referenceLayout.id,
      configuredStation.tiltAxisAngles,
    );
    const axes = TILT_AXIS_META.map((axis) => {
      const direction = referenceLayout.directions.find(
        (d) => d.angle === referenceAngles[axis.label],
      );
      return {
        ...axis,
        angle: referenceAngles[axis.label],
        intoGround: direction?.intoGround ?? false,
      };
    });
    const conflicts = tiltmeterStations.filter((station) => {
      if (station.id === configuredStation.id) {
        return false;
      }
      const stationLayout = getTiltAxisLayout(station.tiltAxisLayout);
      if (stationLayout.id !== referenceLayout.id) {
        return true;
      }
      const stationAngles = getEffectiveTiltAngles(
        stationLayout.id,
        station.tiltAxisAngles,
      );
      return TILT_AXIS_META.some(
        (axis) => stationAngles[axis.label] !== referenceAngles[axis.label],
      );
    });

    return { tiltAxes: axes, conflictingTiltStations: conflicts };
  }, [stations]);

  const fitBounds = React.useCallback((map: any, stationList: Station[]) => {
    const validStations = stationList.filter((s) => s.lat && s.lon);
    if (validStations.length === 0) {
      return;
    }

    const bounds = new (window as any).google.maps.LatLngBounds();
    validStations.forEach((station) => {
      bounds.extend({ lat: station.lat, lng: station.lon });
    });

    map.fitBounds(bounds);
  }, []);

  const onMapLoad = React.useCallback(
    (map: any) => {
      setMapInstance(map);
      if (stations.length > 0) {
        fitBounds(map, stations);
      }
    },
    [stations, fitBounds],
  );

  React.useEffect(() => {
    if (!mapInstance || stations.length === 0) {
      return;
    }
    fitBounds(mapInstance, stations);
  }, [stations, mapInstance, fitBounds]);

  if (loadError) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="danger">
            {gettext("Failed to load Google Maps")}
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  if (!isLoaded || !mapData) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-3">{gettext("Loading map...")}</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Body>
        <h4 className="mb-3">{mapData.organization_map[0].name}</h4>

        {loading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" className="me-2" />
            {gettext("Loading stations...")}
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        <div ref={wrapperRef} style={{ position: "relative" }}>
          <GoogleMap
            mapContainerStyle={{ ...mapContainerStyle, height: mapHeight }}
            center={center}
            zoom={14}
            options={mapOptions}
            onLoad={onMapLoad}
          >
            {stations.map((station) => {
              if (!station.lat || !station.lon) {
                return null;
              }

              const isGnss = station.stationType === "gnss";
              const isTiltmeter = station.stationType === "tiltmeter";

              let markerIcon: google.maps.Icon | null = null;
              const showArrow = hasArrowData(station);
              if (showArrow) {
                const arrowDirection = station.arrowData!
                  .directionCategory as ArrowDirection;
                const arrowLength = station.arrowData!
                  .lengthCategory as ArrowLength;
                markerIcon = createArrowMarkerIcon(arrowDirection, arrowLength);
              } else if (isTiltmeter) {
                markerIcon = createTiltmeterMarkerIcon();
              }

              return (
                <React.Fragment key={station.id}>
                  <Marker
                    position={{ lat: station.lat, lng: station.lon }}
                    title={station.name}
                    icon={markerIcon ?? undefined}
                    onClick={() => {
                      if (isGnss) {
                        navigate(`/stations/${station.identifier}`);
                      } else {
                        toggleInfoWindow(station.id);
                      }
                    }}
                  />
                  {!isGnss && isInfoWindowOpen(station.id) && (
                    <StationInfoWindow
                      station={station}
                      onClose={() => toggleInfoWindow(station.id)}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </GoogleMap>
          <MapLegend
            showGnss={hasGnssStations}
            showTiltmeter={hasTiltmeterStations}
            tiltAxes={tiltAxes}
            conflictingTiltStations={conflictingTiltStations.map((station) => ({
              id: station.id,
              identifier: station.identifier,
            }))}
          />
        </div>

        {!loading && stations.length === 0 && !error && (
          <Alert variant="info" className="mt-3">
            {gettext("No stations found for this map.")}
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
};

export default OrganizationMapPage;
